export type ColorMode = 'light' | 'dark';

class ModeState {
    public current: ColorMode = $state('light');
    public isManual = false;
}

class ModeServiceClass {
    /**
     * Key and body classes must match the inline script in frontend/index.html. That script sets the mode before
     * the first paint, so the wrong mode never flashes up — this service only keeps the same state afterwards.
     */
    private readonly StorageKey = 'LyColorMode';
    private readonly SystemQuery = '(prefers-color-scheme: dark)';
    private readonly BodyClasses: Record<ColorMode, string> = {
        light: 'LyLightMode',
        dark: 'LyDarkMode',
    };

    private readonly State: ModeState = new ModeState();
    private IsInitialized = false;

    public constructor() {
        const Stored = this.readStoredMode();

        this.State.current = Stored ?? this.readSystemMode();
        this.State.isManual = Stored !== null;
    }

    public getMode(): ColorMode {
        return this.State.current;
    }

    public isMode(mode: ColorMode): boolean {
        return this.State.current === mode;
    }

    /** Once chosen by hand, the choice beats the operating system. */
    public setMode(mode: ColorMode): void {
        this.State.isManual = true;
        this.State.current = mode;
        this.applyMode(mode);

        try {
            window.localStorage.setItem(this.StorageKey, mode);
        } catch {
            // Then the mode only lasts for this session.
        }
    }

    /** Call once at start: follows system changes as long as nothing was chosen by hand. */
    public init(): void {
        if (this.IsInitialized) {
            return;
        }
        this.IsInitialized = true;

        // -
        window.matchMedia(this.SystemQuery).addEventListener('change', (event: MediaQueryListEvent): void => {
            if (this.State.isManual) {
                return;
            }

            this.State.current = event.matches ? 'dark' : 'light';
            this.applyMode(this.State.current);
        });

        this.applyMode(this.State.current);
    }

    private applyMode(mode: ColorMode): void {
        document.body.className = this.BodyClasses[mode];
    }

    private readStoredMode(): ColorMode | null {
        try {
            const Stored = window.localStorage.getItem(this.StorageKey);
            return this.isColorMode(Stored) ? Stored : null;
        } catch {
            // No access to the storage — then without it.
            return null;
        }
    }

    private readSystemMode(): ColorMode {
        return window.matchMedia(this.SystemQuery).matches ? 'dark' : 'light';
    }

    private isColorMode(value: string | null): value is ColorMode {
        return value !== null && Object.hasOwn(this.BodyClasses, value);
    }
}

export const ModeService = new ModeServiceClass();
